import { FC, useRef, useCallback, ReactNode } from "react";
import {
  ChatInput,
  ChatInputHandle,
  ChatInputParams,
} from "@web-src/modules/chats/components/ChatInput";
import { useAppVariant, useTranslations } from "@jugl-web/utils";
import { VisitorIdentifiedBanner } from "@web-src/modules/contacts/ContactsPage/components/VisitorIdentifiedBanner";
import { useClientSupportPageContext } from "../../../../providers/ClientSupportPageProvider/useClientSupportPageContext";
import { useWebChatBotAccount } from "../../../../providers/WebChatBotAccountProvider/useWebChatBotAccount";
import { DisconnectedChatInput } from "../DisconnectedChatInput";
import { useManageAccountsDrawer } from "../../../ManageAccountsDrawer/providers/ManageAccountsDrawerContext";

export const WebChatInput: FC<{
  chatId: string;
  conversationName: string;
  onMessageSent?: () => void;
}> = ({ chatId, conversationName, onMessageSent }) => {
  const { sendWebMessage, setIsSendingMessage } = useClientSupportPageContext();
  const { openManageAccountDrawer } = useManageAccountsDrawer();
  const chatInput$ = useRef<ChatInputHandle>(null);
  const { isConnected } = useWebChatBotAccount();
  const { t } = useTranslations();
  const { isCx } = useAppVariant();

  const handleSendMessage: ChatInputParams["onSubmit"] = useCallback(
    async ({ body }) => {
      if (!body.trim()) return;

      try {
        setIsSendingMessage(true);

        sendWebMessage({
          conv_id: chatId,
          conv_name: conversationName,
          text: body,
        });

        setTimeout(() => {
          chatInput$.current?.setValue("");
        }, 100);
        chatInput$.current?.focus();
        onMessageSent?.();
      } catch (error) {
        // NOTE: pass
      } finally {
        setIsSendingMessage(false);
      }
    },
    [
      chatId,
      conversationName,
      sendWebMessage,
      onMessageSent,
      setIsSendingMessage,
    ]
  );

  if (!isConnected) {
    return (
      <DisconnectedChatInput
        title={t({
          id: "client-support-page.account-is-disconnected",
          defaultMessage: "Account is Disconnected",
        })}
        description={t(
          {
            id: "client-support-page.please-enable-to-continue-using-web-chat-inbox",
            defaultMessage:
              "Please <highlightedText>enable</highlightedText> to continue using Web Chat Inbox",
          },
          {
            highlightedText: (text: ReactNode) => (
              <span className="font-medium text-primary">{text}</span>
            ),
          }
        )}
        buttonProps={{
          text: t({
            id: "client-support-page.open-settings",
            defaultMessage: "Open Settings",
          }),
          onClick: () => openManageAccountDrawer({ name: "webChatBotAccount" }),
        }}
      />
    );
  }

  return (
    <>
      {isCx && <VisitorIdentifiedBanner />}
      <div className="border-0 border-t border-solid border-dark-100 px-8 py-6">
        <ChatInput
          variant="inbox"
          id={chatId}
          ref={chatInput$}
          onSubmit={handleSendMessage}
          isLoading={false}
          toolbarActions={{
            emoji: {
              enabled: true,
            },
            attachments: {
              enabled: false,
            },
          }}
        />
      </div>
    </>
  );
};
